import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  Box,
  Icon,
  IconContainer,
  H2,
  Button,
  ButtonUnstyled,
  ExpandableTile,
  P,
} from '@allied-solutions/affinity'
import {
  AutoSolid,
  Home02,
  Upload01,
  QuestionCircle,
} from '@allied-solutions/affinity-react-icons/standard'
import { DashboardTileContentMobile } from './DashboardTileContentMobile'
import { DashboardTileContent } from './DashboardTileContent'
import { useWindowWidth } from '../../hooks'
import { getRequirementChipCount } from '../../hooks/useGetCoverageRequirements'
import { theme } from '../../theme'

const CONDO_SECONDARY_CLASSIFICATIONS = new Set(['COND', 'COCOND'])

const hasUnmetCoverageRequirementStatus = coverageRequirementsData => {
  let accounts = []
  if (Array.isArray(coverageRequirementsData)) {
    accounts = coverageRequirementsData
  } else if (coverageRequirementsData) {
    accounts = [coverageRequirementsData]
  }

  return accounts.some(account =>
    (account?.CoverageRequirements ?? []).some(req => req.Status === false)
  )
}

const StyledExpandableTile = styled(ExpandableTile)`
  border-radius: ${props => props.theme.radii[6]};

  &:has(.collapse-control-button[aria-expanded='false']) {
    border-bottom-left-radius: ${props => props.theme.radii[6]};
    border-bottom-right-radius: ${props => props.theme.radii[6]};
  }

  & .tile-content {
    border-radius: ${props => props.theme.sizes[6]};

    &:has(.collapse-control-button[aria-expanded='false']) {
      border-bottom-left-radius: ${props => props.theme.radii[6]};
      border-bottom-right-radius: ${props => props.theme.radii[6]};
    }
  }

  & .collapse-control-button {
    &[aria-expanded='true'] .icon-container-control {
      background: ${props => props.theme.colors.blue800};

      & .iconSize-md svg {
        color: ${props => props.theme.colors.white};
      }
    }
  }

  ${props => `
    ${props.theme.mediaQueries.md} {
      & .collapse-control-button .icon-container-control {
        height: 2.5rem;
        width: 2.5rem;
      }

      & header #update-insurance-button {
        margin-right: -${props.theme.space['lg']};
      }
    }
  `}
`

const StyledH2 = styled(H2)`
  flex: 1 1 0%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`

const IconWithBadgeWrapper = styled.span`
  position: relative;
  display: inline-flex;
`

const ErrorBadge = styled.span`
  position: absolute;
  top: -1px;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors?.error};
  border: 2px solid white;
  box-sizing: border-box;
`

export const DashboardTile = ({
  borrowerData,
  refId,
  openUploadModal,
  onActiveCoverageReferenceIdChange,
  onOpenHelp,
  coverageRequirementsData,
  isLoadingCoverageRequirements,
  coverageRequirementsError,
  isCoverageRequirementsError,
  isExpanded,
}) => {
  const windowWidth = useWindowWidth()
  const isMobile = windowWidth < Number.parseInt(theme.breakpoints[1])

  const unmetRequirementsCount = getRequirementChipCount(
    coverageRequirementsData
  )

  const isCondoSecondaryClassification = CONDO_SECONDARY_CLASSIFICATIONS.has(
    borrowerData?.SecondaryClassification
  )
  const hasCondoErrorStatusMessage =
    isCondoSecondaryClassification &&
    hasUnmetCoverageRequirementStatus(coverageRequirementsData)

  const coverageRequirementsProps = {
    coverageRequirementsData,
    isLoadingCoverageRequirements,
    coverageRequirementsError,
    isCoverageRequirementsError,
  }

  const getIcon = contractType => {
    switch (contractType) {
      case 'Mortgage':
        return (
          <IconWithBadgeWrapper>
            <IconContainer
              icon={Home02}
              color="violet"
              size={isMobile ? 'sm' : 'md'}
            />
            {unmetRequirementsCount > 0 && <ErrorBadge />}
          </IconWithBadgeWrapper>
        )
      case 'Vehicle':
        return (
          <IconWithBadgeWrapper>
            <IconContainer
              icon={AutoSolid}
              color="teal"
              size={isMobile ? 'sm' : 'md'}
            />
            {unmetRequirementsCount > 0 && <ErrorBadge />}
          </IconWithBadgeWrapper>
        )
      default:
        return null
    }
  }

  const tileContentProps = {
    borrowerData,
    ...coverageRequirementsProps,
    unmetRequirementsCount,
    openUploadModal,
    onActiveCoverageReferenceIdChange,
    onOpenHelp,
  }

  let tileContent
  if (hasCondoErrorStatusMessage) {
    tileContent = (
      <>
        <P typeStyle="bodyMedium.bold" mb="0" color="text.default">
          We currently cannot show the status of this collateral.
        </P>
        <P
          typeStyle="labelSmall.regular"
          mb="0"
          maxWidth="600px"
          color="text.secondary"
        >
          Please contact Support if you would like more information about the
          review process or any documentation that may be required.
        </P>
        <Box mt="2xl" width={['100%', null, null, '180px']}>
          <ButtonUnstyled
            id="dashboard-tile-contact-support"
            type="button"
            typeStyle="buttonMedium"
            color="text.brand"
            display="flex"
            alignItems="center"
            justifyContent={['center', null, 'flex-start']}
            width="100%"
            gap="sm"
            onClick={() => onOpenHelp?.()}
          >
            <Icon src={QuestionCircle} color="text.brand" />
            Contact Support
          </ButtonUnstyled>
        </Box>
      </>
    )
  } else if (isMobile) {
    tileContent = <DashboardTileContentMobile {...tileContentProps} />
  } else {
    tileContent = <DashboardTileContent {...tileContentProps} />
  }

  return (
    <StyledExpandableTile
      id="dashboard-insurance-tile"
      defaultOpen={isExpanded}
      header={{
        title: (
          <Box
            as="span"
            display="flex"
            alignItems="center"
            minWidth={0}
            flex="1 1 auto"
            gap="0"
          >
            {getIcon(borrowerData?.ContractType)}
            <StyledH2 typeStyle="titleMedium" fontWeight="700" mb="0" ml="md">
              {borrowerData?.PropertyDescription?.replaceAll(/\r\n\r\n/g, ' | ')
                .replaceAll(/[\r\n]+/g, ' ')
                .trim()}
            </StyledH2>
          </Box>
        ),
        children:
          isMobile || hasCondoErrorStatusMessage ? null : (
            <Button
              id="update-insurance-button"
              squared
              icon={Upload01}
              size="md"
              outlined
              minWidth="170px"
              onClick={() => openUploadModal()}
            >
              Update Insurance
            </Button>
          ),
      }}
    >
      <StyledExpandableTile.Content p="4xl">
        {tileContent}
      </StyledExpandableTile.Content>
      {isMobile && !hasCondoErrorStatusMessage && (
        <Box
          py="2xl"
          px="4xl"
          borderTop="1px solid"
          borderColor="border.defaultLight"
          display="flex"
          justifyContent="center"
        >
          <Button
            id="update-insurance-button"
            squared
            icon={Upload01}
            size="md"
            outlined
            minWidth="100%"
            onClick={() => openUploadModal()}
          >
            Update Insurance
          </Button>
        </Box>
      )}
    </StyledExpandableTile>
  )
}

DashboardTile.propTypes = {
  borrowerData: PropTypes.object.isRequired,
  refId: PropTypes.string,
  openUploadModal: PropTypes.func.isRequired,
  onActiveCoverageReferenceIdChange: PropTypes.func,
  onOpenHelp: PropTypes.func,
  coverageRequirementsData: PropTypes.array,
  isLoadingCoverageRequirements: PropTypes.bool,
  coverageRequirementsError: PropTypes.object,
  isCoverageRequirementsError: PropTypes.bool,
}
